// Shared display helpers used across KingdomConnect features.
export function fullName(m) { return `${m.firstName} ${m.lastName}`; }
export function initials(name) {
  return (name || "").split(" ").map(w => w[0]).join("").toUpperCase().slice(0, 2);
}

// Format a member's 7-digit number for display: `#0000123`. Returns "—" when
// the number is missing (e.g. Visitor / Guest, or a pre-migration record).
export function formatMemberNumber(n) {
  if (n === null || n === undefined || n === "") return "—";
  const s = String(n).trim();
  if (!s) return "—";
  // Strip any leading "#" a caller might have baked in.
  const raw = s.startsWith("#") ? s.slice(1) : s;
  // If numeric, left-pad to 7. Otherwise return the raw string (with #).
  if (/^\d+$/.test(raw)) return "#" + raw.padStart(7, "0");
  return "#" + raw;
}

// Parse a date value WITHOUT applying any UTC timezone offset.
// `YYYY-MM-DD` strings (the format used by <input type="date">) are stored
// as calendar dates with no time component — they MUST be treated as local
// dates, otherwise viewers west of UTC see the day shift back by one.
// Falls back to `new Date(v)` for anything that isn't a plain `YYYY-MM-DD`.
export function parseDateOnly(v) {
  if (!v) return null;
  if (v instanceof Date) return v;
  const s = String(v);
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(s);
  if (m) return new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3]));
  return new Date(s);
}

export function formatDate(d) {
  if (!d) return "—";
  const parsed = parseDateOnly(d);
  if (!parsed || Number.isNaN(parsed.getTime())) return "—";
  return parsed.toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });
}
// Same as formatDate but without the year — used when a member has hidden
// their birth year via privacy settings (month and day must still show).
export function formatMonthDay(d) {
  if (!d) return "—";
  const parsed = parseDateOnly(d);
  if (!parsed || Number.isNaN(parsed.getTime())) return "—";
  return parsed.toLocaleDateString("en-US", { month: "short", day: "numeric" });
}
export function age(dob) {
  if (!dob) return "—";
  const d = parseDateOnly(dob); if (!d) return "—";
  const now = new Date();
  let a = now.getFullYear() - d.getFullYear();
  if (now.getMonth() < d.getMonth() || (now.getMonth() === d.getMonth() && now.getDate() < d.getDate())) a--;
  return a;
}
export function uid() { return "x" + Math.random().toString(36).slice(2, 10); }
export function upcomingBirthdays(members, days = 7) {
  const today = new Date(); today.setHours(0, 0, 0, 0);
  return (members || []).filter(m => {
    if (!m.dob) return false;
    // Privacy: exclude members who have hidden their birthday entirely
    // OR whose profile is fully restricted from the directory.
    if (m.hideBirthday === true) return false;
    if (m.profileHidden === true) return false;
    const bday = parseDateOnly(m.dob); if (!bday) return false;
    const next = new Date(today.getFullYear(), bday.getMonth(), bday.getDate());
    if (next < today) next.setFullYear(today.getFullYear() + 1);
    const diff = Math.floor((next - today) / 86400000);
    return diff <= days;
  }).map(m => {
    const bday = parseDateOnly(m.dob);
    const next = new Date(today.getFullYear(), bday.getMonth(), bday.getDate());
    if (next < today) next.setFullYear(today.getFullYear() + 1);
    const diff = Math.floor((next - today) / 86400000);
    return { ...m, daysUntilBirthday: diff };
  }).sort((a, b) => a.daysUntilBirthday - b.daysUntilBirthday);
}

// Alphabetical sort: last name (case-insensitive), then first name.
// Used to anchor EVERY dropdown / search-result list of people across the
// app, so finding a specific person is fast and predictable.
function _cmp(a, b) { return (a || "").localeCompare(b || "", undefined, { sensitivity: "base" }); }
export function sortMembersByName(list) {
  return [...(list || [])].sort((a, b) => _cmp(a.lastName, b.lastName) || _cmp(a.firstName, b.firstName));
}
// Users may not have firstName/lastName — fall back to linked-member name
// (resolved by the caller) or username. Pass a memberLookup fn to use the
// linked-member name first; otherwise we just sort by username.
export function sortUsersByName(list, memberLookup) {
  const key = (u) => {
    if (memberLookup) {
      const m = memberLookup(u);
      if (m && m.lastName) return [m.lastName, m.firstName || ""];
    }
    // No linked member → split a "First Last" display name if present,
    // else fall back to username so we still get a stable alphabetical order.
    const display = (u && (u.name || u.username)) || "";
    const parts = display.trim().split(/\s+/);
    if (parts.length >= 2) return [parts[parts.length - 1], parts.slice(0, -1).join(" ")];
    return [display, ""];
  };
  return [...(list || [])].sort((a, b) => {
    const [aLast, aFirst] = key(a);
    const [bLast, bFirst] = key(b);
    return _cmp(aLast, bLast) || _cmp(aFirst, bFirst);
  });
}
